import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, Clock, User, Plus, ChevronLeft, ChevronRight, MoreVertical, Grid, List, CalendarDays, MapPin, Search, X } from 'lucide-react';
import CreateAppointmentModal from './CreateAppointmentModal';
import { toast } from 'react-toastify';
import './styles/AppointmentCalendar.css';
import { OFFICES, generateTimeSlots , TIME_SLOTS_CONFIG, CALENDAR_VIEWS, APPOINTMENT_DEFAULTS, UI_CONSTANTS, ERROR_MESSAGES, LUNCH_BREAK_DEFAULTS } from '../../constants/calendar';
import { AppointmentService } from '../../services/appointmentService';
import { AppointmentServiceUtils } from '../../utils/appointmentUtils';
import { appointmentSyncService } from '../../services/appointmentSyncService';
import { getStylistAppointments, getOfficeAppointments, getHolidaysForDay } from '../../utils/appointmentUtils';
import { getCurrentUser } from '../../utils/auth';
import { API_URL } from '../../config/api';
import CalendarHeader from './CalendarHeader';
import DayView from './views/DayView';
import WeekView from './views/WeekView';
import MonthView from './views/MonthView';
import { formatDateWithWeekday, combineDateAndTimeSlot, startOfWeek, toAPIDate, toLocalDateTime, addDays, addMonths, addHours, formatDate } from '../../utils/dateUtils';
import { isIsraelUser } from '../../utils/locationUtils';



const AppointmentCalendar = ({ user, onLoginRequired }) => {
   // Core state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState('');
  
  // View and selection state
  const [view, setView] = useState('day');
  const [selectedOffice, setSelectedOffice] = useState('pomona');
  const [selectedStylist, setSelectedStylist] = useState(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPrefillData, setModalPrefillData] = useState({});
  const [editingAppointment, setEditingAppointment] = useState(null);
  


  const [highlightedAppointment, setHighlightedAppointment] = useState(null);

  // Deep-linking: jump straight to an appointment when arriving via ?appointmentId=&date=
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingJumpId, setPendingJumpId] = useState(null);

  // Per-stylist lunch break settings: { [stylistId]: { enabled, startHour, startMinute, duration } }
  const [lunchBreakSettings, setLunchBreakSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('lunchBreakSettingsV2');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {};
  });

  const updateStylistLunchSettings = (stylistId, updates) => {
    setLunchBreakSettings(prev => {
      const current = prev[stylistId] || {
        enabled: false,
        startHour: LUNCH_BREAK_DEFAULTS.START_HOUR,
        startMinute: LUNCH_BREAK_DEFAULTS.START_MINUTE,
        duration: LUNCH_BREAK_DEFAULTS.DURATION_MINUTES
      };
      const next = { ...prev, [stylistId]: { ...current, ...updates } };
      try { localStorage.setItem('lunchBreakSettingsV2', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  // Refs
  const calendarRef = useRef(null);
  const fetchDebounceRef = useRef(null);
  const lastFetchKey = useRef('');

  // Computed values
  const currentOfficeStaff = OFFICES[selectedOffice].staff;
  const useMilitaryTime = isIsraelUser();
  const timeSlots = generateTimeSlots(useMilitaryTime);
  const stylistAppointments = getStylistAppointments(appointments, selectedStylist);
  const dayHolidays = getHolidaysForDay(holidays, currentDate);

    const currentUser = getCurrentUser();

  // Auto-select first stylist when office changes
  useEffect(() => {
    if (currentOfficeStaff.length > 0 && !selectedStylist) {
      setSelectedStylist(currentOfficeStaff[0]);
    } else if (selectedStylist && !currentOfficeStaff.find(s => s.id === selectedStylist.id)) {
      setSelectedStylist(currentOfficeStaff[0]);
    }
  }, [selectedOffice, currentOfficeStaff, selectedStylist]);

  // Deep link from another page (e.g. Rescheduled Appointments): ?appointmentId=123&date=2026-07-21
  // Jump to that date so the fetch below picks up the target appointment, then wait for it to load.
  useEffect(() => {
    const appointmentId = searchParams.get('appointmentId');
    const date = searchParams.get('date');
    if (appointmentId && date) {
      setCurrentDate(new Date(`${date}T12:00:00`));
      setView('day');
      setPendingJumpId(appointmentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);





useEffect(() => {
  const timeoutId = setTimeout(() => {
    if (searchQuery) {
      const performSearch = async () => {
        setIsSearching(true);
        try {
          const results = await AppointmentService.searchAppointments(searchQuery);
          setSearchResults(results);
          setShowSearchResults(true);
        } catch (error) {
          console.error('Search error:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };

      performSearch();
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, 300);

  return () => clearTimeout(timeoutId);
}, [searchQuery]);

const handleDayClick = (day) => {
  // Set the selected date to the clicked day
  setCurrentDate(day);
  
  // Optionally switch to day view when a day is clicked from mini month view
  if (view !== 'day') {
    setView('day');
  }
  
  // If you want to close any open modals or clear selections when navigating
  // setIsModalOpen(false);
  // setEditingAppointment(null);
};


const getViewDays = (date, viewType) => {
  const days = [];

  if (viewType === 'day') {
    days.push(new Date(date));
  } else if (viewType === 'week') {
    // Use the startOfWeek utility
    const weekStart = startOfWeek(date);
    for (let i = 0; i < 7; i++) {
      days.push(addDays(weekStart, i));
    }
  } else if (viewType === 'month') {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const monthStart = startOfWeek(firstDay); // Use utility instead of manual calculation

    for (let i = 0; i < 42; i++) { // 6 weeks
      days.push(addDays(monthStart, i));
    }
  }

  return days;
};

  const viewDays = getViewDays(currentDate, view);


  // Create datetime string with proper timezone handling
const createDateTime = (date, timeSlot) => {
  let hours, minutes;

  // Check if timeSlot is in 12-hour format (contains AM/PM)
  if (timeSlot.includes('AM') || timeSlot.includes('PM')) {
    // Use the existing utility to parse 12-hour format
    const parsed = AppointmentServiceUtils.parseTimeSlot(timeSlot);
    hours = parsed.hours;
    minutes = parsed.minutes;
  } else {
    // Assume 24-hour format
    [hours, minutes] = timeSlot.split(':');
    hours = parseInt(hours);
    minutes = parseInt(minutes);
  }

  const datetime = new Date(date);
  datetime.setHours(hours, minutes, 0, 0);

  return toLocalDateTime(datetime);
};

const fetchAppointments = async () => {
  // Only show loading spinner on initial load
  if (initialLoad) {
    setLoading(true);
  }

  try {
    let startDate, endDate;

    if (view === 'day') {
      startDate = toAPIDate(viewDays[0]);
      endDate = toAPIDate(addDays(viewDays[0], 1));
    } else {
      // For week and month views, add 1 day to include the full last day
      startDate = toAPIDate(viewDays[0]);
      endDate = toAPIDate(addDays(viewDays[viewDays.length - 1], 1));
    }

    const appointments = await AppointmentService.fetchAppointments(startDate, endDate);

    // Filter out cancelled appointments
    const activeAppointments = appointments.filter(apt =>
      apt.AppointmentStatus !== 'Cancelled'
    );

    setAppointments(activeAppointments);

    // Mark initial load as complete
    if (initialLoad) {
      setInitialLoad(false);
    }
  } catch (err) {
    setError(err.message);
    console.error('Error fetching appointments:', err);
  } finally {
    if (initialLoad) {
      setLoading(false);
    }
  }
};

const fetchHolidays = async () => {
  try {
    const startDate = toAPIDate(viewDays[0]);
    const endDate = toAPIDate(viewDays[viewDays.length - 1]);
    const holidays = await AppointmentService.fetchHolidays(startDate, endDate);
    setHolidays(holidays);
  } catch (error) {
    console.error('Error fetching holidays:', error);
  }
};


useEffect(() => {
  if (viewDays.length === 0) return;

  // Create a unique key for this fetch request to avoid duplicates
  const fetchKey = `${currentDate.toISOString()}-${view}-${selectedOffice}`;

  // Skip if we already fetched this exact configuration
  if (fetchKey === lastFetchKey.current) return;

  // Clear any pending debounced fetch
  if (fetchDebounceRef.current) {
    clearTimeout(fetchDebounceRef.current);
  }

  // For month view, use a small delay to batch with MonthView's debounced navigation
  // MonthView waits 2.5s after scroll stops before calling onMonthChange
  // We add a small delay here to ensure we don't double-fetch
  const debounceMs = view === 'month' ? 150 : 0;

  fetchDebounceRef.current = setTimeout(() => {
    lastFetchKey.current = fetchKey;
    fetchAppointments();
    fetchHolidays();
  }, debounceMs);

  return () => {
    if (fetchDebounceRef.current) {
      clearTimeout(fetchDebounceRef.current);
    }
  };
}, [currentDate, view, selectedOffice]);

  const handleAppointmentUpdated = (updatedAppointment) => {
    // Update the appointment in the appointments array
    setAppointments(prev => 
      prev.map(apt => 
        apt.ID === updatedAppointment.ID ? updatedAppointment : apt
      )
    );
    
    // Optionally refetch to ensure consistency
    fetchAppointments();
  };


const handleAppointmentClick = async (appointment) => {
  setEditingAppointment(appointment);
  
  let customerBDiniCare = false;
  
  // Fetch customer data if ClientID exists
  if (appointment.ClientID) {
  try {
    const response = await fetch(`${API_URL}/api/customers/${appointment.ClientID}`);
    if (response.ok) {
      const customer = await response.json();
      customerBDiniCare = customer.bDiniCare === 1 || customer.bDiniCare === true;
      console.log('Fetched customer bDiniCare:', customerBDiniCare);
    }
  } catch (error) {
    console.error('Error fetching customer data:', error);
  }
}
  
  // Convert appointment times to local timezone for editing
  const startDate = new Date(appointment.EventStart);
  const endDate = new Date(appointment.EventEnd);

  const prefillData = {
    EventStart: toLocalDateTime(startDate),
    EventEnd: toLocalDateTime(endDate),
    EventDesc: appointment.EventDesc,
    ClientName: appointment.ClientName,
    PhoneNum: appointment.PhoneNum,
    ClientID: appointment.ClientID,
    Title: appointment.Title,
    MeetingWith: appointment.MeetingWith,
    MWuserID: appointment.MWuserID,
    AppointmentNotes: appointment.AppointmentNotes,
    bDiniCare: customerBDiniCare
  };
  
  setModalPrefillData(prefillData);
  setIsModalOpen(true);
};




const handleSearchResultSelect = (appointment) => {
  // Navigate to the day view for the selected appointment
  const appointmentDate = new Date(appointment.EventStart);
  setCurrentDate(appointmentDate);
  setView('day');
  
  // Find and set the correct office and stylist
  const foundOffice = Object.entries(OFFICES).find(([key, office]) => 
    office.staff.some(staff => staff.id === appointment.MWuserID || staff.name === appointment.MeetingWith)
  );
  
  if (foundOffice) {
    setSelectedOffice(foundOffice[0]);
    const stylist = foundOffice[1].staff.find(staff => 
      staff.id === appointment.MWuserID || staff.name === appointment.MeetingWith
    );
    if (stylist) {
      setSelectedStylist(stylist);
    }
  }
  
  // Clear search immediately
  setSearchQuery('');
  setShowSearchResults(false);
  
  // Use the existing scroll logic - faster timeout for snappier response
  setTimeout(() => {
    setHighlightedAppointment(appointment.ID);
    scrollToAppointment(appointment.ID);

    // Remove highlight after 5 seconds
    setTimeout(() => {
      setHighlightedAppointment(null);
    }, 5000);
  }, 500);
};

// Once the deep-linked date's appointments have loaded, find the target and jump to it
useEffect(() => {
  if (!pendingJumpId || appointments.length === 0) return;

  const target = appointments.find(apt => String(apt.ID) === String(pendingJumpId));
  if (target) {
    handleSearchResultSelect(target);
  }

  setPendingJumpId(null);
  setSearchParams({}, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [appointments, pendingJumpId]);

// Helper function to scroll to the highlighted appointment
const scrollToAppointment = (appointmentId, retryCount = 0) => {
  const maxRetries = 5;
  const retryDelay = 150;

  const attemptScroll = () => {
    const appointmentElement = document.querySelector(`[data-appointment-id="${appointmentId}"]`);

    if (!appointmentElement) {
      // Element not found, retry if we haven't exceeded max retries
      if (retryCount < maxRetries) {
        setTimeout(() => scrollToAppointment(appointmentId, retryCount + 1), retryDelay);
      } else {
        console.warn(`Could not find appointment element with ID ${appointmentId} after ${maxRetries} retries`);
      }
      return;
    }

    // Element found, perform scroll
    console.log('Scrolling to appointment:', appointmentId);

    // Try multiple scroll strategies for best compatibility
    // .day-grid-content is the actual scrolling container in day view
    const container = document.querySelector('.day-grid-content') ||
                     document.querySelector('.day-grid-container') ||
                     document.querySelector('.calendar-content') ||
                     document.querySelector('.redesigned-calendar');

    if (container) {
      // Strategy 1: Calculate and scroll within container
      const containerRect = container.getBoundingClientRect();
      const appointmentRect = appointmentElement.getBoundingClientRect();

      // Calculate the appointment's position relative to the container
      const scrollOffset = appointmentRect.top - containerRect.top + container.scrollTop;

      // Position appointment near the top (100px from top for visibility)
      const targetScrollPosition = Math.max(0, scrollOffset - 100);

      container.scrollTo({
        top: targetScrollPosition,
        behavior: 'smooth'
      });

      console.log('Scrolled container to position:', targetScrollPosition);
    } else {
      // Strategy 2: Use scrollIntoView as fallback
      appointmentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });

      console.log('Used scrollIntoView fallback');
    }
  };

  // Initial attempt with minimal delay to ensure DOM is ready
  setTimeout(attemptScroll, 50);
};

  // Navigation
  const navigate = (direction) => {
    if (view === 'day') {
      setCurrentDate(addDays(currentDate, direction));
    } else if (view === 'week') {
      setCurrentDate(addDays(currentDate, direction * 7));
    } else if (view === 'month') {
      setCurrentDate(addMonths(currentDate, direction));
    }
  };




  // Handle month day click - navigate to day view
  const handleMonthDayClick = (date) => {
    setCurrentDate(new Date(date));
    setView('day');
  };





  const handleAppointmentCreated = () => {
    fetchAppointments();
  };

  const getViewTitle = () => {
    if (view === 'day') {
      return formatDateWithWeekday(currentDate);
    } else if (view === 'week') {
      return `${formatDate(viewDays[0])} - ${formatDate(viewDays[6])}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    }
  };

const handleAppointmentDeleted = (appointment) => {
  // Check if this is actually a cancellation/update vs a delete
  if (appointment.AppointmentStatus) {
    // This is an update, not a delete
    setAppointments(prevAppointments =>
      prevAppointments.map(apt =>
        apt.ID === appointment.ID ? appointment : apt
      )
    );

    // Show appropriate toast message based on status
    if (appointment.AppointmentStatus === 'Cancelled') {
      toast.success('Appointment cancelled successfully');
    } else {
      toast.success('Appointment status updated successfully');
    }
  } else {
    // This is an actual delete (if you still have delete functionality)
    setAppointments(prevAppointments =>
      prevAppointments.filter(apt => apt.ID !== appointment.ID)
    );
    toast.success('Appointment deleted successfully');
  }

  // Refresh the calendar view
  fetchAppointments();
};

// Week view drag handlers - only change the date, keep the time the same
const [draggedAppointment, setDraggedAppointment] = useState(null);

const handleWeekDragStart = (e, appointment) => {
  setDraggedAppointment(appointment);
  e.dataTransfer.effectAllowed = 'move';
  e.target.classList.add('dragging');
};

const handleWeekDragEnd = async (e, targetDate) => {
  e.target.classList.remove('dragging');

  // If targetDate is provided, this is a drop event
  if (targetDate && draggedAppointment) {
    try {
      // Get the original appointment times
      const originalStart = new Date(draggedAppointment.EventStart);
      const originalEnd = new Date(draggedAppointment.EventEnd);

      // Extract the time components from the original appointment
      const startHours = originalStart.getHours();
      const startMinutes = originalStart.getMinutes();
      const endHours = originalEnd.getHours();
      const endMinutes = originalEnd.getMinutes();

      // Create new dates with the target date but keeping the original times
      const newStartTime = new Date(targetDate);
      newStartTime.setHours(startHours, startMinutes, 0, 0);

      const newEndTime = new Date(targetDate);
      newEndTime.setHours(endHours, endMinutes, 0, 0);

      // Convert to local datetime strings for the API
      const newStartDateTime = toLocalDateTime(newStartTime);
      const newEndDateTime = toLocalDateTime(newEndTime);

      // 🔗 Use sync service to update appointment AND linked order
      console.log(`🔄 Syncing appointment ${draggedAppointment.ID} to new date:`, { newStartDateTime, newEndDateTime });
      const syncResult = await appointmentSyncService.syncAppointmentToOrder(
        draggedAppointment.ID,
        newStartDateTime,
        newEndDateTime
      );

      if (syncResult.success) {
        // Update local state
        setAppointments(prevAppointments =>
          prevAppointments.map(apt =>
            apt.ID === draggedAppointment.ID
              ? {
                  ...apt,
                  EventStart: newStartDateTime,
                  EventEnd: newEndDateTime,
                  EnteredBy: syncResult.enteredBy || apt.EnteredBy
                }
              : apt
          )
        );

        if (syncResult.orderSynced) {
          toast.success(`Appointment moved and order ${syncResult.linkedOrderNo} synced`);
        } else {
          toast.success('Appointment moved successfully');
        }
      } else {
        throw new Error(syncResult.error || 'Failed to sync appointment');
      }

      // Refresh appointments to ensure UI is in sync
      fetchAppointments();
    } catch (error) {
      console.error('Error moving appointment:', error);
      if (error.status === 401 && onLoginRequired) {
        onLoginRequired(() => handleWeekDragEnd(e, targetDate));
      } else {
        toast.error('Failed to move appointment');
      }
    }
  }

  setDraggedAppointment(null);
};

const renderContent = () => {
  if (view === 'day') {
    // Debug the filtering process
    console.log('=== HOLIDAY DEBUG ===');
    console.log('Raw holidays:', holidays);
    console.log('Current date:', currentDate);
    
    const dayHolidaysResult = holidays ? getHolidaysForDay(holidays, currentDate) : [];
    console.log('Filtered holidays for day:', dayHolidaysResult);
    console.log('=== END HOLIDAY DEBUG ===');
    
    return (
      <DayView
        currentDate={currentDate}
        appointments={appointments}
        setAppointments={setAppointments}
        timeSlots={timeSlots}
        currentOfficeStaff={currentOfficeStaff}
        selectedStylist={selectedStylist}
        dayHolidays={dayHolidaysResult}
        highlightedAppointment={highlightedAppointment}
        onTimeSlotClick={handleTimeSlotClick}
        onAppointmentClick={handleAppointmentClick}
        onError={setError}
        useMilitaryTime={useMilitaryTime}
        lunchBreakSettings={lunchBreakSettings}
        onLunchBreakClick={handleLunchBreakClick}
        onLunchBreakSettingsChange={updateStylistLunchSettings}
      />
    );
  } else if (view === 'week') {
    return (
      <WeekView
        viewDays={viewDays}
        appointments={appointments}
        holidays={holidays}
        selectedStylist={selectedStylist}
        highlightedAppointment={highlightedAppointment}
        onDayClick={handleWeekDayClick}
        onAppointmentClick={handleAppointmentClick}
        onDragStart={handleWeekDragStart}
        onDragEnd={handleWeekDragEnd}
        useMilitaryTime={useMilitaryTime}
      />
    );
  } else {
    return (
      <MonthView
        viewDays={viewDays}
        appointments={getOfficeAppointments(appointments, currentOfficeStaff)}
        holidays={holidays}
        currentDate={currentDate}
        onDayClick={handleMonthDayClick}
        onMonthChange={navigate}
        currentOfficeStaff={currentOfficeStaff}
      />
    );
  }
};

const handleLunchBreakClick = (date, stylist) => {
  const s = lunchBreakSettings[stylist.id] || {};
  const startHour = s.startHour ?? LUNCH_BREAK_DEFAULTS.START_HOUR;
  const startMinute = s.startMinute ?? LUNCH_BREAK_DEFAULTS.START_MINUTE;
  const duration = s.duration ?? LUNCH_BREAK_DEFAULTS.DURATION_MINUTES;

  const startTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), startHour, startMinute, 0);
  const endTime = new Date(startTime.getTime() + duration * 60 * 1000);

  setModalPrefillData({
    EventStart: toLocalDateTime(startTime),
    EventEnd: toLocalDateTime(endTime),
    Title: 'Lunch',
    MeetingWith: stylist.name,
    MWuserID: stylist.id
  });
  setEditingAppointment(null);
  setIsModalOpen(true);
};

const handleTimeSlotClick = (date, timeSlot, stylist = selectedStylist) => {
  if (!stylist) return;
  
  // Create start time
  const startTime = combineDateAndTimeSlot(date, timeSlot);
  const startDateTime = toLocalDateTime(startTime);
  
  // Add 1 hour for end time
  const endTime = addHours(startTime, 1);
  const endDateTime = toLocalDateTime(endTime);

  setModalPrefillData({
    EventStart: startDateTime,
    EventEnd: endDateTime,
    Title: 'customer pickup',
    MeetingWith: stylist.name,
    MWuserID: stylist.id
  });
  setEditingAppointment(null);
  setIsModalOpen(true);
};

  // Add this function to handle week day clicks
  const handleWeekDayClick = (date) => {
    if (!selectedStylist) {
      setError('Please select a stylist first');
      return;
    }

    // Set default time to 9 AM for new appointments
    const startDateTime = createDateTime(date, '09:00');
    const endDateTime = createDateTime(date, '10:00'); // Default 1 hour appointment

    setModalPrefillData({
      EventStart: startDateTime,
      EventEnd: endDateTime,
      Title: 'customer pickup',
      MeetingWith: selectedStylist.name,
      MWuserID: selectedStylist.id
    });
    setEditingAppointment(null);
    setIsModalOpen(true);
  };



  return (
    <div className="redesigned-calendar">
      {/* Header */}
      <CalendarHeader
        currentDate={currentDate}
        view={view}
        getViewTitle={getViewTitle}
        onNavigate={navigate}
        onViewChange={setView}
        onTodayClick={() => setCurrentDate(new Date())}
        selectedOffice={selectedOffice}
        onOfficeChange={setSelectedOffice}
        currentOfficeStaff={currentOfficeStaff}
        selectedStylist={selectedStylist}
        onStylistChange={setSelectedStylist}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        searchResults={searchResults}
        showSearchResults={showSearchResults}
        isSearching={isSearching}
        onSearchResultSelect={handleSearchResultSelect}
        onSearchClear={() => setShowSearchResults(false)}
        appointments={appointments}
        holidays={holidays}
        onDayClick={handleDayClick}
        scrollToAppointment={scrollToAppointment}
        user={user}
        onLoginRequired={onLoginRequired}
      />

      {((view === 'day' && window.innerWidth <= 768) || view === 'week') && (
  <div className="stylist-selection-container">
    {/* Desktop: Tabs */}
    <div className="stylist-tabs desktop-only">
      {currentOfficeStaff.map((stylist) => (
        <div
          key={stylist.id}
          className={`stylist-tab ${selectedStylist?.id === stylist.id ? 'active' : ''}`}
          onClick={() => setSelectedStylist(stylist)}
        >
          <User className="w-4 h-4" />
          {stylist.name}
        </div>
      ))}
    </div>
  </div>
)}

      {/* Error Message */}
      {error && <div className="error-message">{error}</div>}

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Loading appointments...</span>
        </div>
      )}

      {/* Calendar Content */}
      <div className="calendar-content">
         {renderContent()}
      </div>

     {/* Create/Edit Appointment Modal */}
      <CreateAppointmentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAppointment(null);
        }}
        prefillData={modalPrefillData}
        onAppointmentCreated={handleAppointmentCreated}
        onAppointmentUpdated={handleAppointmentUpdated}
        onAppointmentDeleted={handleAppointmentDeleted}
        customTitle={editingAppointment ? 'Edit Appointment' : 'Create New Appointment'}
        currentUser={currentUser}
        editingAppointment={editingAppointment}
        onLoginRequired={onLoginRequired}
      />
      
    </div>
  );
};

export default AppointmentCalendar;